import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { internalWalletService } from '@/services/core/internalWalletService.js';

const CloverContext = createContext({});

export const CloverProvider = ({ children }) => {
  const [cloverWallet, setCloverWallet] = useState(null);
  const [cloverWallets, setCloverWallets] = useState([]);
  const [cloverBalance, setCloverBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  // Refs para evitar re-renders e chamadas duplicadas no fetch
  const walletDataRef = useRef(null);
  const cloverWalletsRef = useRef([]);
  const hasFetchedRef = useRef(false);
  const fetchPromiseRef = useRef(null);

  /**
   * @param {boolean} [force=false] Se true, ignora cache e busca saldo atual na API (ex.: após reserve/confirm).
   */
  const fetchCloverWallet = useCallback(async (force = false) => {
    if (!force && hasFetchedRef.current) return walletDataRef.current;
    if (!force && fetchPromiseRef.current) return fetchPromiseRef.current;

    if (force) {
      walletDataRef.current = null;
      hasFetchedRef.current = false;
      fetchPromiseRef.current = null;
    }

    fetchPromiseRef.current = (async () => {
      setLoading(true);
      try {
        const wallets = await internalWalletService.getInternalWalletBalances();
        const allWallets = Array.isArray(wallets) ? wallets : [];
        const chances = allWallets.find(w => w.code === 'CHANCES') ?? null;
        walletDataRef.current = chances;
        cloverWalletsRef.current = allWallets;
        hasFetchedRef.current = true;
        setCloverWallets(allWallets);
        setCloverWallet(chances);
        setCloverBalance(chances?.currentBalance ?? null);
        return chances;
      } catch (error) {
        console.error('Error fetching clover wallet:', error);
        return null;
      } finally {
        fetchPromiseRef.current = null;
        setLoading(false);
      }
    })();

    return fetchPromiseRef.current;
  }, []); // deps vazias — lê de refs, não de estado

  const decrementCloverBalance = useCallback(() => {
    setCloverBalance(prev => Math.max(0, (prev ?? 0) - 1));
  }, []);

  const value = useMemo(
    () => ({ cloverWallet, cloverWallets, cloverWalletsRef, cloverBalance, loading, fetchCloverWallet, decrementCloverBalance }),
    [cloverWallet, cloverWallets, cloverBalance, loading, fetchCloverWallet, decrementCloverBalance]
  );

  return (
    <CloverContext.Provider value={value}>
      {children}
    </CloverContext.Provider>
  );
};

export const useClover = () => {
  const context = useContext(CloverContext);
  if (!context) {
    throw new Error('useClover must be used within a CloverProvider');
  }
  return context;
};
